import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowRight,
  ArrowsClockwise,
  CircleNotch,
  PaperPlaneTilt,
  Robot,
  User,
  X,
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

export default function ChatWorkerModal({ workerId, workerName, workerIcon, orgId, isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setMessages([]);
      setInput('');
      setSending(false);
      setSessionId(null);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', text }]);

    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/automations/${workerId}/chat`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: text, session_id: sessionId }),
        },
        orgId,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      if (data.session_id) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'worker', text: data.reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'worker', text: `Error: ${err.message}`, isError: true }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setSessionId(null);
    setSending(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col overflow-hidden"
          style={{ height: 'min(600px, 80vh)' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="chat-worker-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-[rgba(255,255,255,0.08)] shrink-0">
            <div className="flex items-center gap-2.5">
              {workerIcon ? (
                <img src={workerIcon} alt="" className="w-7 h-7 rounded-lg object-cover border border-[rgba(255,255,255,0.08)]" />
              ) : (
                <div className="w-7 h-7 rounded-lg bg-[rgba(0,229,192,0.1)] border border-[rgba(0,229,192,0.2)] flex items-center justify-center">
                  <Robot size={14} className="text-[#00E5C0]" />
                </div>
              )}
              <div>
                <span className="text-sm font-semibold text-[#F0F4F8]">{workerName || 'Chat Worker'}</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E5C0]" />
                  <span className="text-[10px] text-[#7D8896]">Test conversation</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button onClick={resetConversation} data-testid="chat-reset-btn"
                  className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.04)] text-[#7D8896] hover:text-[#00E5C0] transition-colors" title="Reset conversation">
                  <ArrowsClockwise size={14} />
                </button>
              )}
              <button onClick={onClose} data-testid="chat-close-btn" className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.04)] text-[#7D8896] hover:text-white transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && !sending && (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <div className="w-12 h-12 rounded-2xl bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.12)] flex items-center justify-center mb-3">
                  <Robot size={22} className="text-[#00E5C0]" />
                </div>
                <p className="text-sm font-medium text-[#B0B8C4] mb-1">Start a conversation</p>
                <p className="text-[11px] text-[#5C6778] max-w-xs">Type a message to see how {workerName || 'this worker'} responds. No real actions will be taken.</p>
              </div>
            )}

            <AnimatePresence initial={false}>
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`chat-message-${i}`}
                >
                  {msg.role === 'worker' && (
                    <div className="w-6 h-6 rounded-lg bg-[rgba(0,229,192,0.1)] border border-[rgba(0,229,192,0.15)] flex items-center justify-center shrink-0 mt-0.5">
                      <Robot size={12} className="text-[#00E5C0]" />
                    </div>
                  )}
                  <div className={`max-w-[80%] px-3.5 py-2.5 rounded-xl text-[13px] leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[rgba(0,229,192,0.12)] text-[#F0F4F8] rounded-br-sm'
                      : msg.isError
                      ? 'bg-[rgba(239,68,68,0.08)] text-red-400 border border-[rgba(239,68,68,0.15)] rounded-bl-sm'
                      : 'bg-[rgba(255,255,255,0.04)] text-[#C8D1DC] border border-[rgba(255,255,255,0.06)] rounded-bl-sm'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-6 h-6 rounded-lg bg-[rgba(99,102,241,0.12)] flex items-center justify-center shrink-0 mt-0.5">
                      <User size={12} weight="fill" className="text-[#818CF8]" />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Typing indicator */}
            {sending && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2.5 items-start">
                <div className="w-6 h-6 rounded-lg bg-[rgba(0,229,192,0.1)] border border-[rgba(0,229,192,0.15)] flex items-center justify-center shrink-0">
                  <Robot size={12} className="text-[#00E5C0]" />
                </div>
                <div className="px-3.5 py-2.5 rounded-xl rounded-bl-sm bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.06)]">
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5C6778] animate-pulse" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5C6778] animate-pulse" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5C6778] animate-pulse" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-[rgba(255,255,255,0.08)] bg-[rgba(0,0,0,0.15)] shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Type a message..."
                disabled={sending}
                data-testid="chat-input"
                className="flex-1 px-4 py-2.5 bg-[#1A2232] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.3)] transition-colors disabled:opacity-50"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || sending}
                data-testid="chat-send-btn"
                className="w-10 h-10 rounded-xl bg-[#00E5C0] hover:bg-[#00D4B1] flex items-center justify-center transition-colors disabled:opacity-25 shrink-0"
              >
                {sending ? <CircleNotch size={16} className="text-[#0A1018] animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" className="text-[#0A1018]" />}
              </button>
            </div>
            <p className="text-[10px] text-[#4A5568] mt-1.5 text-center">Test mode — no real actions will be taken</p>
          </div>
        </motion.div>
      </div>
    </>
  );
}
